import { useEffect, useMemo, useRef, useState } from "react";
import useAxios from "axios-hooks";
import {
  ChoiceGroup,
  DefaultButton,
  Dialog,
  Dropdown,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  Stack,
  Text,
  TextField
} from "@fluentui/react";
import DetailsTable from "../../../Table/DetailsTable";
import SsoCobrandSelector from "./SsoCobrandSelector";

const SOURCE_TYPE_COPY = "CopyFromCobrand";

const formatUtcDate = (dateValue) => {
  if (!dateValue || dateValue === "0001-01-01T00:00:00") {
    return "N/A";
  }

  const date = new Date(dateValue);

  if (Number.isNaN(date.getTime())) {
    return "N/A";
  }

  return `${date.toLocaleDateString()} ${date.toLocaleTimeString()} UTC`;
};

const getApiErrorMessage = (error, fallbackMessage) => {
  const responseData = error?.response?.data;
  const errors = responseData?.errors;

  if (Array.isArray(errors) && errors.length > 0) {
    const firstError = errors[0];

    if (typeof firstError === "string") {
      return firstError;
    }

    if (firstError?.errorMessage) {
      return firstError.errorMessage;
    }
  }

  if (typeof responseData === "string") {
    return responseData;
  }

  if (responseData?.message) {
    return responseData.message;
  }

  return fallbackMessage;
};

const ConfigureCobrandIdpDialog = ({
  hidden,
  selectedCobrandId,
  cobrandOptions,
  existingEntityIds,
  onDismiss,
  onCreated
}) => {
  const [metadataSourceType, setMetadataSourceType] = useState("FileCopyPaste");
  const [metadataInput, setMetadataInput] = useState("");
  const [metadataUrlInput, setMetadataUrlInput] = useState("");
  const [inCommonEntityId, setInCommonEntityId] = useState("");
  const [fetchedMetadataXml, setFetchedMetadataXml] = useState("");
  const [metadataParseResult, setMetadataParseResult] = useState(null);
  const [metadataFlowError, setMetadataFlowError] = useState("");
  const [metadataFlowInfo, setMetadataFlowInfo] = useState("");
  const [showReplaceConfirmDialog, setShowReplaceConfirmDialog] = useState(false);
  const [entityIdConfirmationText, setEntityIdConfirmationText] = useState("");
  const [sourceCobrandOption, setSourceCobrandOption] = useState(null);
  const [sourceCobrandIdps, setSourceCobrandIdps] = useState([]);
  const [sourceCobrandIdpId, setSourceCobrandIdpId] = useState(null);
  // Incremented to invalidate in-flight source IdP loads
  const sourceRequestIdRef = useRef(0);

  const isCopyMode = metadataSourceType === SOURCE_TYPE_COPY;

  const [{ loading: loadingSourceIdps }, fetchSourceCobrandIdps] = useAxios(
    { url: "/internal/cobrand/0/cobrandidps" },
    { manual: true }
  );
  const [{ loading: copyingIdp }, copyCobrandIdp] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/copy", method: "POST" },
    { manual: true }
  );

  const [{ loading: fetchingMetadata }, fetchMetadata] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/fetch-metadata", method: "POST" },
    { manual: true }
  );
  const [{ loading: parsingMetadata }, parseMetadata] = useAxios(
    { url: "/internal/cobrand/0/cobrandidp/parse-metadata", method: "POST" },
    { manual: true }
  );
  const [{ loading: creatingIdp }, createCobrandIdp] = useAxios(
    { url: "/internal/cobrand/0/create-cobrandidp", method: "POST" },
    { manual: true }
  );

  useEffect(() => {
    if (hidden) {
      sourceRequestIdRef.current += 1;
      setMetadataSourceType("FileCopyPaste");
      setMetadataInput("");
      setMetadataUrlInput("");
      setInCommonEntityId("");
      setFetchedMetadataXml("");
      setMetadataParseResult(null);
      setMetadataFlowError("");
      setMetadataFlowInfo("");
      setEntityIdConfirmationText("");
      setShowReplaceConfirmDialog(false);
      setSourceCobrandOption(null);
      setSourceCobrandIdps([]);
      setSourceCobrandIdpId(null);
    }
  }, [hidden]);

  const sourceCobrandOptions = useMemo(
    () => (cobrandOptions || []).filter((option) => option.value !== selectedCobrandId),
    [cobrandOptions, selectedCobrandId]
  );

  const metadataCertificateColumns = useMemo(
    () => [
      {
        key: "use",
        name: "Use",
        fieldName: "use",
        minWidth: 90,
        maxWidth: 120,
        isResizable: true
      },
      {
        key: "status",
        name: "Status",
        fieldName: "status",
        minWidth: 140,
        maxWidth: 180,
        isResizable: true
      },
      {
        key: "expirationDateUtc",
        name: "Expires (UTC)",
        fieldName: "expirationDateUtc",
        minWidth: 180,
        maxWidth: 220,
        isResizable: true,
        onRender: (item) => formatUtcDate(item.expirationDateUtc)
      },
      {
        key: "thumbprint",
        name: "Thumbprint",
        fieldName: "thumbprint",
        minWidth: 220,
        maxWidth: 320,
        isResizable: true
      },
      {
        key: "subject",
        name: "Subject",
        fieldName: "subject",
        minWidth: 260,
        maxWidth: 420,
        isResizable: true,
        onRender: (item) => <span style={{ wordBreak: "break-word" }}>{item.subject || "N/A"}</span>
      }
    ],
    []
  );

  const resetMetadataFlowState = () => {
    setFetchedMetadataXml("");
    setMetadataParseResult(null);
    setMetadataFlowError("");
    setMetadataFlowInfo("");
    setEntityIdConfirmationText("");
    setShowReplaceConfirmDialog(false);
  };

  const handleMetadataSourceTypeChange = (_, option) => {
    sourceRequestIdRef.current += 1;
    setMetadataSourceType(option?.key || "FileCopyPaste");
    resetMetadataFlowState();
    setSourceCobrandOption(null);
    setSourceCobrandIdps([]);
    setSourceCobrandIdpId(null);
  };

  // Reuses the parsed-metadata preview so copy and metadata flows share the same review/confirm UI
  const selectSourceCobrandIdp = (sourceIdp) => {
    setSourceCobrandIdpId(sourceIdp?.cobrandIdpId || null);
    setMetadataParseResult(sourceIdp ? { ...sourceIdp, existingEntityIds: existingEntityIds || [] } : null);
    setEntityIdConfirmationText("");
  };

  const handleSourceCobrandChange = async (option) => {
    const requestId = ++sourceRequestIdRef.current;
    setSourceCobrandOption(option || null);
    setSourceCobrandIdps([]);
    selectSourceCobrandIdp(null);
    setMetadataFlowError("");
    setMetadataFlowInfo("");

    if (!option?.value) {
      return;
    }

    try {
      const response = await fetchSourceCobrandIdps({ url: `/internal/cobrand/${option.value}/cobrandidps` });

      if (requestId !== sourceRequestIdRef.current) {
        return;
      }

      const idps = response?.data || [];
      setSourceCobrandIdps(idps);

      if (idps.length === 0) {
        setMetadataFlowError(`${option.label} has no IdP configuration to copy.`);
      } else if (idps.length === 1) {
        selectSourceCobrandIdp(idps[0]);
      }
    } catch (error) {
      if (requestId === sourceRequestIdRef.current) {
        setMetadataFlowError(getApiErrorMessage(error, "Unable to load IdPs for the source cobrand."));
      }
    }
  };

  const handleCopyIdp = async (confirmationText = "") => {
    setMetadataFlowError("");
    setMetadataFlowInfo("");

    if (!selectedCobrandId || !sourceCobrandIdpId) {
      setMetadataFlowError("Select a source cobrand IdP to copy.");
      return;
    }

    try {
      await copyCobrandIdp({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/copy`,
        data: {
          sourceCobrandIdpId,
          entityIdConfirmationText: confirmationText || null
        }
      });

      setShowReplaceConfirmDialog(false);
      await onCreated();
      onDismiss();
    } catch (error) {
      setMetadataFlowError(getApiErrorMessage(error, "Unable to copy Cobrand IdP."));
    }
  };

  const handleMetadataFileSelected = (event) => {
    const file = event.target?.files?.[0];

    if (!file) {
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      setMetadataInput(reader.result?.toString() || "");
      setMetadataFlowInfo(`Loaded ${file.name}.`);
      setMetadataFlowError("");
      setFetchedMetadataXml("");
      setMetadataParseResult(null);
    };

    reader.onerror = () => {
      setMetadataFlowError("Unable to read the selected metadata file.");
    };

    reader.readAsText(file);
    event.target.value = "";
  };

  const handleFetchMetadata = async () => {
    setMetadataFlowError("");
    setMetadataFlowInfo("");
    setFetchedMetadataXml("");
    setMetadataParseResult(null);

    if (!selectedCobrandId) {
      setMetadataFlowError("Select a cobrand first.");
      return;
    }

    try {
      const response = await fetchMetadata({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/fetch-metadata`,
        data: {
          sourceType: metadataSourceType,
          metadataXml: metadataSourceType === "FileCopyPaste" ? metadataInput : null,
          metadataUrl: metadataSourceType === "Url" ? metadataUrlInput : null,
          inCommonEntityId: metadataSourceType === "InCommonFederation" ? inCommonEntityId : null
        }
      });

      const xml = response?.data?.metadataXml || "";
      setFetchedMetadataXml(xml);
      setMetadataFlowInfo("Metadata fetched successfully. Review and parse before creating the IdP record.");
    } catch (error) {
      setMetadataFlowError(getApiErrorMessage(error, "Unable to fetch metadata."));
    }
  };

  const handleParseMetadata = async () => {
    setMetadataFlowError("");
    setMetadataFlowInfo("");
    setMetadataParseResult(null);

    const metadataForParse = (fetchedMetadataXml || metadataInput || "").trim();

    if (!selectedCobrandId) {
      setMetadataFlowError("Select a cobrand first.");
      return;
    }

    if (!metadataForParse) {
      setMetadataFlowError("Provide metadata first by loading a file, pasting XML, or fetching metadata.");
      return;
    }

    try {
      const response = await parseMetadata({
        url: `/internal/cobrand/${selectedCobrandId}/cobrandidp/parse-metadata`,
        data: {
          metadataXml: metadataForParse
        }
      });

      setMetadataParseResult(response?.data || null);
      setMetadataFlowInfo("Metadata parsed successfully. You can now create the IdP record.");
    } catch (error) {
      setMetadataFlowError(getApiErrorMessage(error, "Unable to parse metadata."));
    }
  };

  const handleCreateIdp = async (confirmationText = "") => {
    setMetadataFlowError("");
    setMetadataFlowInfo("");

    const metadataForSave = (fetchedMetadataXml || metadataInput || "").trim();

    if (!selectedCobrandId) {
      setMetadataFlowError("Select a cobrand first.");
      return;
    }

    if (!metadataParseResult) {
      setMetadataFlowError("Parse metadata before creating the IdP record.");
      return;
    }

    if (!metadataForSave) {
      setMetadataFlowError("Metadata is missing. Load/fetch metadata and parse before creating the IdP record.");
      return;
    }

    try {
      const formData = new FormData();
      formData.append("metadataXml", metadataForSave);

      if (confirmationText) {
        formData.append("entityIdConfirmationText", confirmationText);
      }

      await createCobrandIdp({
        url: `/internal/cobrand/${selectedCobrandId}/create-cobrandidp`,
        data: formData,
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });

      setShowReplaceConfirmDialog(false);
      await onCreated();
      onDismiss();
    } catch (error) {
      const errorMessage = getApiErrorMessage(error, "Unable to create Cobrand IdP.");
      setMetadataFlowError(errorMessage);

      // If the backend requires confirmation, always show the confirmation dialog
      if (
        errorMessage.toLowerCase().includes("requires typing") ||
        errorMessage.toLowerCase().includes("entity id") ||
        errorMessage.toLowerCase().includes("already exists")
      ) {
        setShowReplaceConfirmDialog(true);
      }
    }
  };

  const handleClearParsedMetadata = () => {
    setMetadataParseResult(null);
    setMetadataFlowError("");
    setMetadataFlowInfo("");
    setShowReplaceConfirmDialog(false);
    setEntityIdConfirmationText("");
  };

  // Only show 'Replace' if the parsed EntityId matches an existing one for this cobrand
  const isReplace = !!(
    metadataParseResult?.entityId &&
    Array.isArray(metadataParseResult.existingEntityIds) &&
    metadataParseResult.existingEntityIds.some(
      (eid) => eid?.trim().toLowerCase() === metadataParseResult.entityId.trim().toLowerCase()
    )
  );
  // Always update dialog title and button label reactively
  const createOrReplaceLabel = isReplace ? "Replace" : "Add";
  const dialogTitle = isReplace ? "Replace Existing IdP" : "Add New IdP";

  const isSaving = creatingIdp || copyingIdp;
  const saveIdp = isCopyMode ? handleCopyIdp : handleCreateIdp;

  // Intercept Replace click to require confirmation before backend call
  const handlePrimaryButtonClick = () => {
    if (isReplace) {
      setShowReplaceConfirmDialog(true);
    } else {
      saveIdp("");
    }
  };

  return (
    <>
      <Dialog
        hidden={hidden}
        onDismiss={onDismiss}
        minWidth={800}
        maxWidth={1050}
        modalProps={{ isBlocking: false }}
        dialogContentProps={{
          type: DialogType.normal,
          title: dialogTitle,
          subText: "Fetch metadata server-side, parse it, then save the IdP for the selected cobrand. Multiple IdPs are now supported. If you add an IdP with an EntityId that already exists, you will be prompted to confirm replacement."
        }}
      >
        <Stack tokens={{ childrenGap: 10 }}>
          <ChoiceGroup
            label="Get metadata from"
            selectedKey={metadataSourceType}
            options={[
              { key: "FileCopyPaste", text: "File or Copy/Paste" },
              { key: "Url", text: "URL" },
              { key: "InCommonFederation", text: "InCommon Federation" },
              { key: SOURCE_TYPE_COPY, text: "Copy from another cobrand" }
            ]}
            onChange={handleMetadataSourceTypeChange}
          />

          {isCopyMode && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="small">
                Use this when a partner (e.g. WebMD) manages several cobrands with the same IdP. The source IdP settings and certificates are copied to this cobrand.
              </Text>
              <SsoCobrandSelector
                label="Source Cobrand"
                options={sourceCobrandOptions}
                value={sourceCobrandOption}
                onChange={handleSourceCobrandChange}
                loading={loadingSourceIdps}
                disabled={isSaving}
              />
              {sourceCobrandIdps.length > 1 && (
                <Dropdown
                  label="Source IdP"
                  placeholder="Select the IdP to copy"
                  selectedKey={sourceCobrandIdpId}
                  options={sourceCobrandIdps.map((idp) => ({ key: idp.cobrandIdpId, text: idp.entityId }))}
                  onChange={(_, option) =>
                    selectSourceCobrandIdp(sourceCobrandIdps.find((idp) => idp.cobrandIdpId === option?.key))
                  }
                  disabled={isSaving}
                />
              )}
            </Stack>
          )}

          {metadataSourceType === "Url" && (
            <TextField
              label="Metadata URL (server-side fetch)"
              value={metadataUrlInput}
              onChange={(_, value) => {
                setMetadataUrlInput(value || "");
                setFetchedMetadataXml("");
                setMetadataParseResult(null);
              }}
              placeholder="https://idp.example.edu/metadata"
            />
          )}

          {metadataSourceType === "InCommonFederation" && (
            <TextField
              label="InCommon Entity Id"
              value={inCommonEntityId}
              onChange={(_, value) => {
                setInCommonEntityId(value || "");
                setFetchedMetadataXml("");
                setMetadataParseResult(null);
              }}
              placeholder="urn:mace:incommon:example.edu"
            />
          )}

          {metadataSourceType === "FileCopyPaste" && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Text variant="small">Upload metadata file</Text>
              <input
                type="file"
                accept=".xml,.txt"
                onChange={handleMetadataFileSelected}
              />

              <TextField
                label="Metadata XML"
                multiline
                rows={8}
                value={metadataInput}
                onChange={(_, value) => {
                  setMetadataInput(value || "");
                  setFetchedMetadataXml("");
                  setMetadataParseResult(null);
                }}
              />
            </Stack>
          )}

          {metadataFlowError && (
            <MessageBar messageBarType={MessageBarType.error} isMultiline>
              {metadataFlowError}
            </MessageBar>
          )}

          {metadataFlowInfo && (
            <MessageBar messageBarType={MessageBarType.success} isMultiline={false}>
              {metadataFlowInfo}
            </MessageBar>
          )}

          {!isCopyMode && (
            <Stack horizontal tokens={{ childrenGap: 8 }}>
              {metadataSourceType !== "FileCopyPaste" && (
                <DefaultButton
                  text={fetchingMetadata ? "Fetching..." : "Fetch Metadata"}
                  onClick={handleFetchMetadata}
                  disabled={!selectedCobrandId || fetchingMetadata || parsingMetadata || creatingIdp}
                />
              )}
              <DefaultButton
                text={parsingMetadata ? "Parsing..." : "Parse Metadata"}
                onClick={handleParseMetadata}
                disabled={!(fetchedMetadataXml || metadataInput) || fetchingMetadata || parsingMetadata || creatingIdp}
              />
            </Stack>
          )}

          {fetchedMetadataXml && (
            <TextField
              label="Fetched Metadata (server-side result)"
              multiline
              rows={8}
              readOnly
              value={fetchedMetadataXml}
            />
          )}

          {metadataParseResult && (
            <Stack tokens={{ childrenGap: 8 }}>
              <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Text variant="large">{isCopyMode ? "IdP to copy" : "Parsed metadata"}</Text>
                {!isCopyMode && (
                  <DefaultButton
                    text="Clear"
                    onClick={handleClearParsedMetadata}
                    disabled={creatingIdp}
                  />
                )}
              </Stack>
              <Text variant="small"><b>Entity Id:</b> {metadataParseResult.entityId || "N/A"}</Text>
              <Text variant="small"><b>SSO Binding:</b> {metadataParseResult.singleSignOnServiceBinding || "N/A"}</Text>
              <Text variant="small"><b>SSO URL:</b> {metadataParseResult.singleSignOnServiceUrl || "N/A"}</Text>
              <Text variant="small"><b>SLO Binding:</b> {metadataParseResult.singleLogoutServiceBinding || "N/A"}</Text>
              <Text variant="small"><b>SLO URL:</b> {metadataParseResult.singleLogoutServiceUrl || "N/A"}</Text>

              {isReplace && (
                <MessageBar messageBarType={MessageBarType.warning} isMultiline>
                  <b>Warning:</b> An IdP with this EntityId already exists for this cobrand. Saving will <b>replace</b> the existing IdP with this EntityId.
                </MessageBar>
              )}

              <Stack tokens={{ childrenGap: 2 }}>
                <Text variant="mediumPlus" style={{ marginTop: 16, marginBottom: 0 }}>
                  {isCopyMode ? "Certificates To Copy" : "Certificates Found In Metadata"}
                </Text>

                <DetailsTable
                  items={metadataParseResult.certificates || []}
                  columns={metadataCertificateColumns}
                  hideSelectionDetails
                  hideFilterField
                  hideColumnFilters
                  shouldUsePagination={false}
                  selectionMode="none"
                  style={{ width: "100%", marginTop: 0, marginBottom: 0 }}
                />
              </Stack>
            </Stack>
          )}
        </Stack>

        <DialogFooter>
          <PrimaryButton
            text={isSaving ? "Saving..." : `${createOrReplaceLabel} IdP`}
            onClick={handlePrimaryButtonClick}
            disabled={!metadataParseResult || fetchingMetadata || parsingMetadata || loadingSourceIdps || isSaving || showReplaceConfirmDialog}
          />
          <DefaultButton text="Close" onClick={onDismiss} disabled={isSaving} />
        </DialogFooter>
      </Dialog>

      <Dialog
        hidden={!showReplaceConfirmDialog}
        onDismiss={() => setShowReplaceConfirmDialog(false)}
        dialogContentProps={{
          type: DialogType.normal,
          title: "Confirm IdP Replacement",
          subText: `To replace the existing IdP, type the exact Entity Id below to confirm replacement.\n\nEntity Id: ${metadataParseResult?.entityId || ''}`
        }}
        minWidth={520}
      >
        <Stack tokens={{ childrenGap: 10 }}>
          <TextField
            label="Entity Id confirmation"
            value={entityIdConfirmationText}
            onChange={(_, value) => setEntityIdConfirmationText(value || "")}
            autoComplete="off"
          />
        </Stack>

        <DialogFooter>
          <PrimaryButton
            text={isSaving ? "Saving..." : "Confirm and Replace"}
            onClick={() => saveIdp(entityIdConfirmationText)}
            disabled={isSaving || !entityIdConfirmationText.trim()}
          />
          <DefaultButton
            text="Cancel"
            onClick={() => setShowReplaceConfirmDialog(false)}
            disabled={isSaving}
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default ConfigureCobrandIdpDialog;
